/* eslint-disable no-continue */
/* eslint-disable no-await-in-loop */
import { useMutation } from '@apollo/client';
import { pick, useRouter, useShowToast } from '@application/utils';
import { Analytics } from '@application/utils/analytics';
import { graphql } from '@gql/gql';
import type {
  AccountBuyerGetAnimalsQuery,
  CreateBookingMutation,
  CreateBookingMutationVariables,
} from '@gql/graphql';
import { AnimalStatCreatedFromEnum } from '@gql/graphql';
import type { BookingPageFormValues } from '@presentation/pages/BookingPage/types';
import cleanDeep from 'clean-deep';
import hash from 'object-hash';
import { useCallback } from 'react';
import { useAnimalInitialValuesBuilder } from '../../MtAnimalBuyerPageController/api/useAnimalIntialValuesBuilder';
import { useCreateAnimal } from '../../MtAnimalBuyerPageController/api/useCreateAnimal';
import { useUpdateAnimal } from '../../MtAnimalBuyerPageController/api/useUpdateAnimal';

export const CREATE_BOOKING = graphql(`
  mutation CreateBooking(
    $workerServiceId: Int!
    $bookingReason: String!
    $startDateTime: DateTime!
    $bookingCardInfoProTranslationsWhere: ProTranslationWhereInput!
    $bookingCardInfoServiceTranslationsWhere: ServiceTranslationWhereInput!
    $bookingAnimals: [BookingAnimalCreate!]!
    $phoneNumber: String
    $forMultiAnimals: Boolean
  ) {
    createBooking(
      workerServiceId: $workerServiceId
      bookingReason: $bookingReason
      startDateTime: $startDateTime
      phoneNumber: $phoneNumber
      bookingAnimals: $bookingAnimals
      forMultiAnimals: $forMultiAnimals
    ) {
      ...BookingCardInfo
    }
  }
`);

export interface CreateBookingFromFormParams extends BookingPageFormValues {
  workerServiceId: number;
  workerId: number;
  accountAnimals: NonNullable<AccountBuyerGetAnimalsQuery['accountMe']>['animals'] | undefined;
  forMultiAnimals: boolean;
  onAnimalsMutation?: (animalIds: number[]) => void;
}

export const useCreateBooking = () => {
  const { translate, language } = useRouter();
  const { createAnimalFromForm } = useCreateAnimal();
  const { updateAnimalFromForm } = useUpdateAnimal();
  const { showToast } = useShowToast();
  const { animalInitialValuesBuilder } = useAnimalInitialValuesBuilder();

  const [createBookingMutation, { error: mutationError, loading: mutationLoading, data }] =
    useMutation<CreateBookingMutation, CreateBookingMutationVariables>(CREATE_BOOKING);

  const createBookingFromForm = useCallback(
    async (params: CreateBookingFromFormParams) => {
      const {
        bookingReason,
        bookingStart,
        workerServiceId,
        phoneNumber,
        animals,
        accountAnimals,
        onAnimalsMutation,
        animalIds,
        forMultiAnimals,
      } = params;

      const groomerExtraValues = pick(params, [
        'animalRecentlyAdopted',
        'animalFirstTimeAtGroomer',
        'animalHasKnottedFur',
        'animalLastVisit',
        'animalBrushingFrequency',
        'animalTemper',
      ]);

      if (!animals?.length) {
        throw new Error('Cannot create booking! Animals field missing');
      }

      const bookingAnimalsIds: number[] = [];

      for (let index = 0; index < animalIds.length; index += 1) {
        const animalId = animalIds[index];
        const animal = animals[index];
        if (animalId === 'new') {
          const createdAnimal = await createAnimalFromForm({
            form: animal,
            createdFrom: AnimalStatCreatedFromEnum.BOOKING,
          });
          const createdAnimalId = createdAnimal?.data?.createAnimal?.id;
          if (createdAnimalId) {
            bookingAnimalsIds.push(createdAnimalId);
          }

          Analytics.sendAnimalCreatedEvent(animal.type, 'booking');
        } else if (animalId) {
          const animalToUpdate = (accountAnimals ?? []).find((x) => x.id === animalId);
          if (!animalToUpdate) continue;
          const animalInitialValues = animalInitialValuesBuilder(animalToUpdate);
          if (animalInitialValues && hash(animal) !== hash(animalInitialValues)) {
            await updateAnimalFromForm(animal, animalToUpdate);
          }
          bookingAnimalsIds.push(animalId);
        }
      }

      if (!bookingAnimalsIds.length || !bookingReason || !bookingStart) {
        throw new Error('Cannot create booking! Missing required fields');
      }

      onAnimalsMutation?.(bookingAnimalsIds);
      // showToast('ERROR ON PURPOSE!', { type: 'error' });
      // throw new Error('Not implemented');

      await createBookingMutation({
        variables: {
          workerServiceId,
          bookingReason,
          forMultiAnimals,
          bookingAnimals: bookingAnimalsIds.map((id) => ({
            animalId: id,
            bookingReason,
            ...cleanDeep(groomerExtraValues),
          })),
          startDateTime: bookingStart.toISOString(),
          phoneNumber,
          bookingCardInfoProTranslationsWhere: { language: { equals: language } },
          bookingCardInfoServiceTranslationsWhere: { language: { equals: language } },
        },
      });

      showToast(translate('common.toasts.bookingSent'), { type: 'success' });

      Analytics.sendBookingEvent();
    },
    [
      animalInitialValuesBuilder,
      createAnimalFromForm,
      createBookingMutation,
      language,
      showToast,
      translate,
      updateAnimalFromForm,
    ],
  );

  return {
    createBooking: undefined,
    createBookingLoading: mutationLoading,
    createBookingError: mutationError,
    createBookingData: data,
    createBookingFromForm,
  };
};
